import {
  CANADIAN_PROVINCES,
  FILING_FREQUENCIES,
  TRADE_LABELS,
  TRADES,
  type Trade,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Field, Input, PageHeader } from '@hvac/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { completeSetup, startAccount } from '../../shared/api/billing-queries.js';
import { createOrganization, setActiveOrganization } from '../../shared/api/queries.js';
import { SetupConnections } from '../connections/setup-connections.js';
import { UploadDropzone } from '../documents/upload-dropzone.js';
import { UploadQueuePanel } from '../documents/upload-queue-panel.js';
import { useUploadQueue } from '../documents/use-upload-queue.js';

/**
 * Add a business: the questions an accountant would ask, then the first paper.
 *
 * Province selects the tax rules, the fiscal year end shapes every report,
 * the GST/HST number goes on invoices, and the filing frequency sets the
 * return deadlines the firm desk counts down to. Each has its own page for
 * later; asking now means the books are right from the first record. Setup
 * ends with the CRM connected and the first documents dropped, so the
 * business is never an empty screen.
 */

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const FREQUENCY_LABELS: Readonly<Record<(typeof FILING_FREQUENCIES)[number], string>> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly (most small businesses)',
  annual: 'Annually',
};

const selectClass =
  'w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm';

export function NewOrganizationPage() {
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/" className="text-sm text-content-muted hover:text-content">
          ← Back
        </Link>
        <PageHeader
          title={created === null ? 'Add a business' : `${created.name} is ready`}
          description={
            created === null
              ? 'A new client, fully isolated from every other one: its own books, documents, suppliers, tax settings and audit trail from the first row.'
              : 'Two things make the first day useful: the jobs and invoices from the CRM, and a first pile of paper.'
          }
        />
      </div>
      {created === null ? (
        <Questions onCreated={setCreated} />
      ) : (
        <FirstPaper organizationId={created.id} />
      )}
    </div>
  );
}

function Questions({ onCreated }: { onCreated: (created: { id: string; name: string }) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [province, setProvince] = useState('NB');
  const [trade, setTrade] = useState<Trade>('hvac');
  const [yearEnd, setYearEnd] = useState(12);
  const [businessNumber, setBusinessNumber] = useState('');
  const [frequency, setFrequency] = useState<(typeof FILING_FREQUENCIES)[number]>('quarterly');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      // Whatever they said at sign-up becomes their account, before the first
      // business joins it. Harmless when it already exists.
      await startAccount().catch(() => undefined);
      const organization = await createOrganization({ name: name.trim(), province });
      await completeSetup(organization.id, {
        trade,
        fiscalYearEndMonth: yearEnd,
        filingFrequency: frequency,
        ...(businessNumber.trim() === '' ? {} : { businessNumber: businessNumber.trim() }),
      });
      return organization;
    },
    onSuccess: (organization) => {
      void queryClient.invalidateQueries();
      onCreated({ id: organization.id, name: organization.name });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That business could not be created.'),
  });

  return (
    <>
      {error === null ? null : <Alert tone="danger">{error}</Alert>}
      <Card>
        <CardHeader
          title="What an accountant would ask"
          description="Province selects the tax rules. The rest shapes the reports, the invoices and the filing deadlines. All of it can be changed later."
        />
        <CardBody className="grid max-w-2xl gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Business name">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Doucet Heating Ltd."
                />
              )}
            </Field>
          </div>
          <Field label="Province">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={province}
                onChange={(event) => setProvince(event.target.value)}
                className={selectClass}
              >
                {CANADIAN_PROVINCES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Trade">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={trade}
                onChange={(event) => setTrade(event.target.value as Trade)}
                className={selectClass}
              >
                {TRADES.map((key) => (
                  <option key={key} value={key}>
                    {TRADE_LABELS[key]}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Fiscal year ends" hint="The last month of the business's year.">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={yearEnd}
                onChange={(event) => setYearEnd(Number(event.target.value))}
                className={selectClass}
              >
                {MONTHS.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="GST/HST is filed">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={frequency}
                onChange={(event) =>
                  setFrequency(event.target.value as (typeof FILING_FREQUENCIES)[number])
                }
                className={selectClass}
              >
                {FILING_FREQUENCIES.map((key) => (
                  <option key={key} value={key}>
                    {FREQUENCY_LABELS[key]}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="GST/HST number"
              hint="Optional. Nine digits and RT0001; it is printed on invoices."
            >
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={businessNumber}
                  onChange={(event) => setBusinessNumber(event.target.value)}
                  placeholder="123456789 RT0001"
                />
              )}
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Button
              onClick={() => create.mutate()}
              disabled={name.trim().length === 0}
              isLoading={create.isPending}
            >
              Create the business
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function FirstPaper({ organizationId }: { organizationId: string }) {
  const queue = useUploadQueue({ organizationId });
  const finish = () => {
    setActiveOrganization(organizationId);
    // A new business means every cached query is for the wrong one.
    window.location.assign('/');
  };

  return (
    <>
      <SetupConnections organizationId={organizationId} />
      <Card>
        <CardHeader
          title="Drop the first documents"
          description="Receipts, bills, bank and card statements, a payroll register: anything. Each one is read, sorted and turned into a draft for review."
        />
        <CardBody className="flex flex-col gap-4">
          <UploadDropzone onFiles={(files) => queue.add(files)} />
          <UploadQueuePanel queue={queue} />
        </CardBody>
      </Card>
      <div className="flex items-center gap-3">
        <Button onClick={finish} disabled={queue.activeCount > 0}>
          {queue.items.length === 0 ? 'Skip for now, open the books' : 'Open the books'}
        </Button>
        {queue.activeCount > 0 ? (
          <span className="text-sm text-content-muted">Waiting for the uploads to finish…</span>
        ) : null}
      </div>
    </>
  );
}
